/**
 * The FOH Workboard read: one batched fetch per refresh, never one per card.
 *
 *   1  tickets — live (OptiBoard's Live scope) + picked up since today's shop
 *      midnight                                             (2 queries, parallel)
 *   2  logical owners not already loaded                    (0–1 query)
 *   3  in parallel: custody rows in FOH-relevant states, advisor assignments,
 *      commercial states, open pauses of held tickets, handoff events,
 *      current vehicle locations, bay names, parking-spot labels  (8 queries)
 *
 * ≈ 10–11 round trips for a 20–30 ticket board, independent of the number of
 * cards. Large `in` lists are chunked. People's names come from the cached
 * useFohCapableMembers / useCustodyActorNames queries.
 *
 * The query key lives under 'foh-custody-queue', so every existing
 * invalidation of the FOH queue — the custody mutation, custody realtime, the
 * shared ticket-updated signal and the shop's dayboard broadcast (both via
 * useFohQueueCounts) — re-reads the Workboard too. No new channel.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { applyOptiBoardFetchScope } from '@/utils/optiBoardTabPredicates';
import { getTodayInShopTz, shopLocalToUtc } from '@/utils/shopTime';
import { readShopCurrentLocations } from '@/lib/location/api';
import type { CurrentVehicleLocation } from '@/lib/location/model';
import type { LocationNames } from '@/lib/location/labels';
import { FOH_QUEUE_STATUSES } from '@/components/lifecycle/FohCustodyQueue';
import {
  HANDOFF_EVENT_KINDS,
  latestMeaningfulHandoff,
  toCustodyHistoryEntries,
  type CustodyHistoryEntry,
  type CustodyHistoryRow,
} from '@/lib/tickets/lifecycle/custodyHistory';
import {
  buildLogicalRepairOrders,
  FOH_LANE_TICKET_COLUMNS,
  isTombstoneSegment,
  logicalOwnerId,
  type LogicalRepairOrder,
  type WorkboardAssignment,
  type WorkboardBusinessState,
  type WorkboardCustody,
  type WorkboardPause,
  type WorkboardTicket,
} from '@/lib/foh/workboard/workboardModel';

/** The lane facts (shared with the nav badge projection) + what a card displays. */
export const WORKBOARD_TICKET_COLUMNS =
  `${FOH_LANE_TICKET_COLUMNS}, repair_number, customer_name, customer_phone, type, `
  + 'vehicle:vehicles(year, make, model, vin, license_plate), '
  + 'technician:technicians!tickets_assigned_tech_id_fkey(id, name)';

export const fohWorkboardQueryKey = (shopId: string | null | undefined) => ['foh-custody-queue', shopId, 'workboard'] as const;

export interface FohWorkboardData {
  repairOrders: LogicalRepairOrder[];
  /** Latest technician → FOH handoff per repair order (the kick, never the accept). */
  handoffs: Map<string, CustodyHistoryEntry>;
  /** Current vehicle location per repair order (live segment first). */
  locations: Map<string, CurrentVehicleLocation>;
  locationNames: LocationNames;
  today: string;
}

const CHUNK = 150;
function chunks<T>(xs: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += CHUNK) out.push(xs.slice(i, i + CHUNK));
  return out;
}
async function inChunks<R>(ids: readonly string[], read: (ids: string[]) => PromiseLike<{ data: unknown; error: unknown }>): Promise<R[]> {
  if (ids.length === 0) return [];
  const results = await Promise.all(chunks(ids).map((c) => read(c)));
  const out: R[] = [];
  for (const r of results) {
    if (r.error) throw r.error;
    out.push(...((r.data ?? []) as R[]));
  }
  return out;
}

export async function fetchFohWorkboard(shopId: string, shopTimezone: string): Promise<FohWorkboardData> {
  const sb = supabase as any;
  const today = getTodayInShopTz(shopTimezone);
  const todayStartUtc = shopLocalToUtc(today, '00:00', shopTimezone);

  const [live, pickedToday] = await Promise.all([
    applyOptiBoardFetchScope(sb.from('tickets').select(WORKBOARD_TICKET_COLUMNS).eq('shop_id', shopId), 'Live'),
    sb.from('tickets').select(WORKBOARD_TICKET_COLUMNS).eq('shop_id', shopId)
      .is('deleted_at', null).gte('picked_up_at', todayStartUtc),
  ]) as Array<{ data: WorkboardTicket[] | null; error: unknown }>;
  if (live.error) throw live.error;
  if (pickedToday.error) throw pickedToday.error;
  const tickets = new Map<string, WorkboardTicket>();
  for (const t of [...(live.data ?? []), ...(pickedToday.data ?? [])]) tickets.set(t.id, t);

  // A live continuation whose owner left on an earlier day (or is otherwise
  // outside both scopes) must still be judged by its owner.
  const missingOwners = [...new Set([...tickets.values()].map((t) => logicalOwnerId(t, tickets)))].filter((id) => !tickets.has(id));
  for (const t of await inChunks<WorkboardTicket>(missingOwners, (ids) =>
    sb.from('tickets').select(WORKBOARD_TICKET_COLUMNS).in('id', ids))) tickets.set(t.id, t);

  const all = [...tickets.values()];
  const ownerIds = [...new Set(all.map((t) => logicalOwnerId(t, tickets)))];
  const heldIds = all.filter((t) => !isTombstoneSegment(t) && (t.status === 'Paused' || t.paused_at != null)).map((t) => t.id);

  const [custodyRes, assignments, businessStates, pauses, locations, bays, spots] = await Promise.all([
    sb.from('ticket_custody').select('ticket_id, custody_owner, custody_status, holder_user_id, entered_at')
      .eq('shop_id', shopId).in('custody_status', FOH_QUEUE_STATUSES as unknown as string[]),
    inChunks<WorkboardAssignment>(ownerIds, (ids) =>
      sb.from('ticket_advisor_assignments').select('ticket_id, advisor_user_id').in('ticket_id', ids)),
    inChunks<WorkboardBusinessState>(ownerIds, (ids) =>
      sb.from('ticket_business_state').select('ticket_id, current_state, entered_at').in('ticket_id', ids)),
    inChunks<WorkboardPause>(heldIds, (ids) =>
      sb.from('ticket_pause_sessions').select('ticket_id, pause_intent, pause_reason, pause_detail')
        .in('ticket_id', ids).is('resumed_at', null).is('resolved_at', null)),
    readShopCurrentLocations(shopId),
    sb.from('shop_bays').select('id, name').eq('shop_id', shopId),
    sb.from('parking_lot_spots').select('id, label').eq('shop_id', shopId),
  ]);
  if (custodyRes.error) throw custodyRes.error;
  const custody = ((custodyRes.data ?? []) as WorkboardCustody[]).filter((c) => tickets.has(c.ticket_id));

  const repairOrders = buildLogicalRepairOrders({ tickets: all, custody, assignments, businessStates, pauses });

  // Handoffs: only for repair orders the front office has touched.
  const custodyTicketIds = [...new Set(custody.map((c) => c.ticket_id))];
  const memberIdsOf = new Map(repairOrders.map((ro) => [ro.id, ro.members.map((m) => m.id)]));
  const handoffIds = repairOrders
    .filter((ro) => ro.custody != null)
    .flatMap((ro) => memberIdsOf.get(ro.id) ?? []);
  const events = await inChunks<CustodyHistoryRow & { ticket_id: string }>(
    [...new Set([...handoffIds, ...custodyTicketIds])],
    (ids) => sb.from('ticket_custody_events')
      .select('id, ticket_id, event_kind, from_owner, to_owner, actor_id, actor_role, reason, metadata, created_at')
      .in('ticket_id', ids).in('event_kind', HANDOFF_EVENT_KINDS as unknown as string[])
      .order('created_at', { ascending: false }),
  );
  const eventsBy = new Map<string, Array<CustodyHistoryRow & { ticket_id: string }>>();
  for (const e of events) {
    const list = eventsBy.get(e.ticket_id) ?? [];
    list.push(e);
    eventsBy.set(e.ticket_id, list);
  }

  const handoffs = new Map<string, CustodyHistoryEntry>();
  const roLocations = new Map<string, CurrentVehicleLocation>();
  for (const ro of repairOrders) {
    const rows = ro.members.flatMap((m) => eventsBy.get(m.id) ?? []);
    const h = latestMeaningfulHandoff(toCustodyHistoryEntries(rows), ['kick_to_foh']);
    if (h) handoffs.set(ro.id, h);
    const loc = locations.get(ro.live.id) ?? ro.members.map((m) => locations.get(m.id)).find(Boolean);
    if (loc) roLocations.set(ro.id, loc);
  }

  const locationNames: LocationNames = {
    bays: new Map(((bays.data ?? []) as Array<{ id: string; name: string }>).map((b) => [b.id, { name: b.name }])),
    spots: new Map(((spots.data ?? []) as Array<{ id: string; label: string }>).map((s) => [s.id, { label: s.label }])),
  };

  return { repairOrders, handoffs, locations: roLocations, locationNames, today };
}

export function useFohWorkboard(shopId: string | null | undefined, shopTimezone: string, enabled = true) {
  return useQuery({
    queryKey: fohWorkboardQueryKey(shopId),
    enabled: enabled && !!shopId && !!shopTimezone,
    retry: false,
    queryFn: () => fetchFohWorkboard(shopId as string, shopTimezone),
  });
}
